import { useEffect, useCallback } from 'react';
import { images } from 'utils/icons';
import COLORS from '../../../utils/constants/colors';
import PropTypes from 'prop-types';

const Drawing = ({
  maps,
  map,
  setValues,
  setShapes,
  shapes,
  selectedType,
  colors = COLORS.ORANGE,
}) => {
  const drawingManager = new maps.drawing.DrawingManager({
    drawingMode: null,
    drawingControl: false,
    markerOptions: {
      draggable: true,
      icon: {
        url: images.iconSite.default,
        anchor: new maps.Point(10, 10),
        scaledSize: new maps.Size(20, 20),
        size: new maps.Size(20, 20),
        origin: new maps.Point(0, 0),
      },
    },
    circleOptions: {
      draggable: true,
      clickable: false,
      editable: true,
      options: {
        strokeColor: colors,
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: colors,
        fillOpacity: 0.3,
      },
    },
    polygonOptions: {
      draggable: true,
      clickable: false,
      editable: true,
    },
    rectangleOptions: {
      draggable: true,
      clickable: false,
      editable: true,
      options: {
        strokeColor: colors,
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: colors,
        fillOpacity: 0.3,
        editable: true,
        draggable: true,
      },
    },
  });

  function updatePolygonBounds(evt) {
    const bounds = [];

    const polygonBounds = evt.overlay.getPath();
    for (let i = 0; i < polygonBounds.length; i += 1) {
      const vertice = {
        latitude: polygonBounds.getAt(i).lat(),
        longitude: polygonBounds.getAt(i).lng(),
      };

      bounds.push(vertice);
    }
    setValues(bounds);
  }

  function updateMarkerLocation(evt) {
    const latitude = evt.overlay.position.lat();
    const longitude = evt.overlay.position.lng();

    setValues({ latitude, longitude });
  }

  function updateCircleBounds(evt) {
    const center = evt.overlay.getCenter();
    const radius = evt.overlay.getRadius();

    setValues({
      center: { latitude: center.lat(), longitude: center.lng() },
      radius,
    });
  }

  function updateRectangleBounds(evt) {
    const bounds = evt.overlay.getBounds();
    const NE = bounds.getNorthEast();
    const SW = bounds.getSouthWest();
    const NW = new maps.LatLng(NE.lat(), SW.lng());
    const SE = new maps.LatLng(SW.lat(), NE.lng());
    const data = [NE, NW, SW, SE];

    const newMap = data.map(d => ({
      latitude: d.lat(),
      longitude: d.lng(),
    }));
    setValues(newMap);
  }

  function updateEllipse(evt, rotation) {
    let currentRotation = rotation;
    const bounds = evt.overlay.getBounds();
    const NE = bounds.getNorthEast();
    const SW = bounds.getSouthWest();

    const major_axis =
      maps.geometry.spherical.computeDistanceBetween(
        NE,
        new maps.LatLng(SW.lat(), NE.lng()),
      ) / 2;
    const minor_axis =
      maps.geometry.spherical.computeDistanceBetween(
        new maps.LatLng(bounds.getCenter().lat(), SW.lng()),
        new maps.LatLng(bounds.getCenter().lat(), NE.lng()),
      ) / 2;

    // === Ellipse ===
    const point = bounds.getCenter(); // maps.LatLng(43,-78);
    if (shapes) {
      shapes.overlay.setMap(null);
    }

    const ellipse = new maps.Polygon.Ellipse({
      point,
      r1: major_axis,
      r2: minor_axis,
      rotation: 10,
      strokeColour: colors,
      strokeWeight: 2,
      strokeOpacity: 1,
      fillColour: colors,
      fillOpacity: 0.5,
      draggable: true,
    });

    setShapes({ overlay: ellipse });
    setValues({
      center: { latitude: point.lat(), longitude: point.lng() },
      major: major_axis,
      minorAxis: minor_axis,
      rotation: currentRotation,
    });

    maps.event.addListener(ellipse, 'click', evt => {
      const origin = ellipse.getCenter();
      const center = evt.latLng;

      currentRotation += 5;
      ellipse.rotate(5, origin, map);

      setValues({
        center: { latitude: center.lat(), longitude: center.lng() },
        major: major_axis,
        minorAxis: minor_axis,
        rotation: currentRotation,
      });
    });

    maps.event.addListener(ellipse, 'dragend', evt => {
      const center = evt.latLng;
      setValues({
        center: { latitude: center.lat(), longitude: center.lng() },
        major: major_axis,
        minorAxis: minor_axis,
        rotation: 10,
      });
    });
    ellipse.setMap(map);
  }

  const manageDrawing = useCallback(
    evt => {
      setShapes(evt);

      const drawingMode = drawingManager.getDrawingMode();

      if (drawingMode === 'polygon') {
        updatePolygonBounds(evt);

        maps.event.addListener(evt.overlay.getPath(), 'insert_at', () =>
          updatePolygonBounds(evt),
        );

        maps.event.addListener(evt.overlay.getPath(), 'set_at', () =>
          updatePolygonBounds(evt),
        );
      }

      if (drawingMode === 'rectangle') {
        if (selectedType === 'ELLIPSE') {
          const rotation = 0;
          updateEllipse(evt, rotation);

          maps.event.addListener(evt.overlay, 'bounds_changed', () => {
            updateEllipse(evt);
          });
        } else {
          updateRectangleBounds(evt);

          maps.event.addListener(evt.overlay, 'bounds_changed', () => {
            updateRectangleBounds(evt);
          });
        }
      }

      if (drawingMode === 'marker') {
        updateMarkerLocation(evt);

        maps.event.addListener(evt.overlay, 'dragend', () => {
          updateMarkerLocation(evt);
        });
      }

      if (drawingMode === 'circle') {
        updateCircleBounds(evt);

        maps.event.addListener(evt.overlay, 'bounds_changed', () => {
          updateCircleBounds(evt);
        });
      }
      drawingManager.setDrawingMode(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedType, colors],
  );

  useEffect(() => {
    const eventListener = maps.event.addListener(
      drawingManager,
      'overlaycomplete',
      manageDrawing,
    );

    if (colors) {
      const options = {
        strokeColor: colors,
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: colors,
        fillOpacity: 0.3,
        editable: true,
        draggable: true,
      };

      drawingManager.setOptions({
        polygonOptions: options,
        rectangleOptions: options,
      });
    }

    if (selectedType) {
      if (shapes) {
        shapes.overlay.setMap(null);
      }

      if (selectedType === 'ELLIPSE') {
        drawingManager.setDrawingMode('rectangle');
        drawingManager.setOptions({
          rectangleOptions: {
            options: {
              strokeOpacity: 0,
              fillOpacity: 0,
            },
          },
        });
        drawingManager.setMap(map);
      } else {
        drawingManager.setMap(map);
        drawingManager.setDrawingMode(selectedType.toLowerCase());
      }
    }

    return () => {
      maps.event.removeListener(eventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageDrawing]);
  return null;
};

Drawing.propTypes = {
  maps: PropTypes.object,
  map: PropTypes.object,
  setValues: PropTypes.func,
  setShapes: PropTypes.func,
  shapes: PropTypes.object,
  selectedType: PropTypes.string,
  colors: PropTypes.string,
};
export default Drawing;
